/**
 * CreateImageElementDialog Component
 * Creates a new Content Hub ImageElement from an asset
 * Features:
 * - Name and description input
 * - Folder selection
 * - Tag input
 * - Alt text and caption input
 * - Preview of the asset
 */

import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import {
  Image as ImageIcon,
  Folder,
  Tag,
  FileText,
  Loader2,
  Check,
  AlertCircle,
} from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { FolderSelector } from '@/components/content-hub/FolderSelector'
import { TagsInput } from '@/components/content-hub/TagsInput'
import { useCreateContentItem } from '@/lib/hooks/use-content-hub'
import type { AssetOData } from '@/lib/api/services/assets'

// ============================================
// Types
// ============================================

export interface CreateImageElementDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  asset: AssetOData | null
}

interface FormData {
  name: string
  description: string
  folderId: string | null
  tags: string[]
  altText: string
  caption: string
}

// ============================================
// CreateImageElementDialog Component
// ============================================

export function CreateImageElementDialog({
  open,
  onOpenChange,
  asset,
}: CreateImageElementDialogProps) {
  const navigate = useNavigate()
  const { toast } = useToast()

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    folderId: null,
    tags: [],
    altText: '',
    caption: '',
  })

  // Validation errors
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({})

  // Reset form when dialog opens/closes or asset changes
  const resetForm = useCallback(() => {
    if (asset) {
      // Use asset name without extension as default name
      const nameWithoutExt = asset.name.replace(/\.[^/.]+$/, '')
      setFormData({
        name: nameWithoutExt,
        description: '',
        folderId: null,
        tags: asset.tags || [],
        altText: '',
        caption: '',
      })
    }
    setErrors({})
  }, [asset])

  // Create mutation
  const createMutation = useCreateContentItem({
    onSuccess: (newItem) => {
      toast({
        title: 'Image Element Created',
        description: `"${newItem.name}" has been created in Content Hub.`,
      })
      onOpenChange(false)
      // Navigate to the new item
      navigate(`/content-hub/item/${newItem.id}`)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create Image Element',
        variant: 'destructive',
      })
    },
  })

  // Handle form changes
  const handleChange = useCallback(
    (field: keyof FormData, value: FormData[keyof FormData]) => {
      setFormData((prev) => ({ ...prev, [field]: value }))
      // Clear error when field is edited
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }))
      }
    },
    [errors]
  )

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.altText.trim()) {
      newErrors.altText = 'Alt text is required for accessibility'
    } else if (formData.altText.length > 125) {
      newErrors.altText = 'Alt text should be under 125 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData])

  // Handle submit
  const handleSubmit = async () => {
    if (!asset) return

    if (!validateForm()) return

    // Create the image element
    // The server will look up asset details (url, thumbnailUrl, fileSize, mimeType) by assetId
    createMutation.mutate({
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      type: 'Image',
      folderId: formData.folderId || undefined,
      tags: formData.tags,
      imageContent: {
        assetId: asset.id,
        altText: formData.altText.trim(),
        caption: formData.caption.trim() || undefined,
      },
    })
  }

  // Handle dialog open change
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      resetForm()
    }
    onOpenChange(newOpen)
  }

  if (!asset) return null

  const isSubmitting = createMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <ImageIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            Create Image Element
          </DialogTitle>
          <DialogDescription>
            Create a new Content Hub Image Element from this asset
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Asset Preview */}
          <div className="flex items-center gap-4 p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700">
            <div className="w-24 h-24 rounded-lg overflow-hidden bg-neutral-200 dark:bg-neutral-700 flex-shrink-0">
              {asset.thumbnailUrl || asset.url ? (
                <img
                  src={asset.thumbnailUrl || asset.url}
                  alt={asset.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="h-8 w-8 text-neutral-400" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-neutral-900 dark:text-white truncate">
                {asset.name}
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                {(asset.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>

          <Separator />

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-neutral-400" />
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Enter a name for this image element"
              className={cn(
                'rounded-xl',
                errors.name && 'border-red-500 focus:border-red-500'
              )}
            />
            {errors.name && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.name}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Add a description..."
              rows={2}
              className="rounded-xl resize-none"
            />
          </div>

          {/* Folder */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Folder className="h-4 w-4 text-neutral-400" />
              Folder (optional)
            </Label>
            <FolderSelector
              value={formData.folderId}
              onChange={(folderId) => handleChange('folderId', folderId)}
              placeholder="Select a folder..."
            />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-neutral-400" />
              Tags (optional)
            </Label>
            <TagsInput
              value={formData.tags}
              onChange={(tags) => handleChange('tags', tags)}
              placeholder="Add tags..."
            />
          </div>

          <Separator />

          {/* Alt Text */}
          <div className="space-y-2">
            <Label htmlFor="altText" className="flex items-center gap-2">
              Alt Text <span className="text-red-500">*</span>
              <span className="text-xs text-neutral-400 font-normal ml-auto">
                {formData.altText.length}/125
              </span>
            </Label>
            <Input
              id="altText"
              value={formData.altText}
              onChange={(e) => handleChange('altText', e.target.value)}
              placeholder="Describe the image for accessibility"
              maxLength={125}
              className={cn(
                'rounded-xl',
                errors.altText && 'border-red-500 focus:border-red-500'
              )}
            />
            {errors.altText && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.altText}
              </p>
            )}
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Alt text helps screen readers describe images to visually impaired users.
            </p>
          </div>

          {/* Caption */}
          <div className="space-y-2">
            <Label htmlFor="caption" className="flex items-center gap-2">
              Caption (optional)
              <span className="text-xs text-neutral-400 font-normal ml-auto">
                {formData.caption.length}/500
              </span>
            </Label>
            <Textarea
              id="caption"
              value={formData.caption}
              onChange={(e) => handleChange('caption', e.target.value)}
              placeholder="Add a caption that appears below the image"
              rows={2}
              maxLength={500}
              className="rounded-xl resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Create Image Element
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CreateImageElementDialog
